import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import { toast } from "sonner";

import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import CatalogueList from "@/pages/CatalogueList";
import CatalogueEditor from "@/pages/CatalogueEditor";
import ProductLibrary from "@/pages/ProductLibrary";
import CoverDesigns from "@/pages/CoverDesigns";
import BrandSettings from "@/pages/BrandSettings";
import Users from "@/pages/Users";
import StudioLayout from "@/studio/StudioLayout";
import StudioDashboard from "@/studio/StudioDashboard";
import ProductWorkbench from "@/studio/ProductWorkbench";
import StudioLibrary from "@/studio/StudioLibrary";
import StudioCatalogueEditor from "@/studio/StudioCatalogueEditor";
import StudioCatalogues from "@/studio/StudioCatalogues";
import StudioCovers from "@/studio/StudioCovers";
import StudioSettings from "@/studio/StudioSettings";
import RevisionHistory from "@/studio/RevisionHistory";

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B3B2E]" data-testid="app-splash">
      <div className="text-center">
        <div className="mx-auto h-40 w-40 grid place-items-center">
          <img src="/pgt-logo-hd.png" alt="Prime Gold Tea" className="h-full w-full object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]" />
        </div>
        <div className="mt-5 h-1 w-32 mx-auto bg-[#C9A34A]/40 rounded-full overflow-hidden">
          <div className="h-full w-1/2 bg-[#C9A34A] animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function Protected({ children, admin }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function CreateRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    api
      .post("/catalogues", { title: "CTC Tea Sample Catalogue" })
      .then((r) => {
        toast.success(`Catalogue ${r.data.catalogue_number} created`);
        navigate(`/catalogue/${r.data.id}`, { replace: true });
      })
      .catch(() => {
        toast.error("Could not create catalogue");
        navigate("/", { replace: true });
      });
  }, [navigate]);
  return <Splash />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route path="/" element={<Dashboard />} />
              <Route path="/create" element={<CreateRedirect />} />
              <Route path="/catalogue/:id" element={<CatalogueEditor />} />
              <Route path="/drafts" element={<CatalogueList status="draft" title="Draft Catalogues" />} />
              <Route path="/ready" element={<CatalogueList status="ready" title="Ready Catalogues" />} />
              <Route path="/shared" element={<CatalogueList status="shared" title="Shared Catalogues" />} />
              <Route path="/archived" element={<CatalogueList status="archived" title="Archived Catalogues" />} />
              <Route path="/library" element={<ProductLibrary />} />
              <Route path="/covers" element={<CoverDesigns />} />
              <Route path="/settings" element={<Protected admin><BrandSettings /></Protected>} />
              <Route path="/users" element={<Protected admin><Users /></Protected>} />
            </Route>
            <Route
              path="/studio"
              element={
                <Protected>
                  <StudioLayout />
                </Protected>
              }
            >
              <Route index element={<StudioDashboard />} />
              <Route path="products/new" element={<ProductWorkbench />} />
              <Route path="products/:id" element={<ProductWorkbench />} />
              <Route path="products/:id/history" element={<RevisionHistory />} />
              <Route path="library" element={<StudioLibrary />} />
              <Route path="catalogues" element={<StudioCatalogues />} />
              <Route path="catalogues/new" element={<StudioCatalogueEditor />} />
              <Route path="catalogues/:id" element={<StudioCatalogueEditor />} />
              <Route path="covers" element={<StudioCovers />} />
              <Route path="settings" element={<StudioSettings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="bottom-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
